import { SectionError, SectionLoading } from '@/components/section-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { AlertTriangle, BadgeDollarSign, Bot, CircleDollarSign, CircleX, FileWarning, Hash, Timer } from 'lucide-react';
import type { ComponentPropsWithoutRef, ReactNode, Ref } from 'react';
import { formatCostPerMinute, formatCurrency, formatDecimal, formatNumber, getQualityGapCount } from './formatting';
import type { UsageDataQuality, UsageSummary } from './types';

const summaryLoadingCardIds = ['cost', 'minute', 'azure', 'events', 'gaps'];

type MetricCardProps = {
  title: string;
  value: string;
  detail: string;
  icon: typeof CircleDollarSign;
  children?: ReactNode;
  ref?: Ref<HTMLDivElement>;
} & Omit<ComponentPropsWithoutRef<typeof Card>, 'children'>;

const MetricCard = ({ title, value, detail, icon: Icon, children, className, ref, ...rest }: MetricCardProps) => (
  <Card ref={ref} className={cn('h-full', className)} {...rest}>
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="font-medium text-muted-foreground text-sm">{title}</CardTitle>
      <Icon className="h-4 w-4 text-primary" />
    </CardHeader>
    <CardContent>
      <div className="font-bold text-2xl">{value}</div>
      <p className="text-muted-foreground text-xs">{detail}</p>
      {children ? <div className="mt-3">{children}</div> : null}
    </CardContent>
  </Card>
);

const rowCount = (rows: UsageDataQuality['missing_pricing']) => rows.reduce((total, row) => total + row.events, 0);

const QualityBadges = ({ quality }: { quality: UsageDataQuality | undefined }) => {
  if (!quality) return null;

  const missingPricingCount = rowCount(quality.missing_pricing);
  const errorCount = rowCount(quality.errors);
  const badges = [
    {
      label: 'Mistókust',
      value: quality.failed_events,
      icon: AlertTriangle,
      variant: quality.failed_events > 0 ? 'destructive' : 'secondary',
    },
    {
      label: 'Vantar kostnað',
      value: quality.missing_cost_events,
      icon: CircleDollarSign,
      variant: quality.missing_cost_events > 0 ? 'destructive' : 'secondary',
    },
    {
      label: 'Vantar hljóðlengd',
      value: quality.missing_audio_duration_events,
      icon: Timer,
      variant: quality.missing_audio_duration_events > 0 ? 'destructive' : 'secondary',
    },
    {
      label: 'Óþekktur veitandi/líkan',
      value: quality.unknown_provider_model_events,
      icon: Bot,
      variant: quality.unknown_provider_model_events > 0 ? 'default' : 'secondary',
    },
    {
      label: 'Vantar verðskrá',
      value: missingPricingCount,
      icon: BadgeDollarSign,
      variant: missingPricingCount > 0 ? 'default' : 'secondary',
    },
    {
      label: 'Villur',
      value: errorCount,
      icon: CircleX,
      variant: errorCount > 0 ? 'destructive' : 'secondary',
    },
  ] as const;

  return (
    <div className="flex flex-wrap gap-1.5">
      {badges
        .filter(({ value, label }) => value > 0 || ['Mistókust', 'Vantar kostnað', 'Vantar hljóðlengd'].includes(label))
        .map(({ label, value, icon: Icon, variant }) => (
          <Tooltip key={label}>
            <TooltipTrigger asChild>
              <Badge
                variant={variant}
                className="gap-1 px-1.5 py-1 tabular-nums"
                aria-label={`${label}: ${formatNumber(value)}`}
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{formatNumber(value)}</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {label}: {formatNumber(value)}
            </TooltipContent>
          </Tooltip>
        ))}
    </div>
  );
};

export const UsageSummaryCards = ({
  summary,
  quality,
  isLoading,
  isError,
  error,
  onRetry,
  hasActiveFilters,
  isQualityLoading,
  isQualityError,
  qualityError,
  onQualityRetry,
}: {
  summary: UsageSummary | undefined;
  quality: UsageDataQuality | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  hasActiveFilters: boolean;
  isQualityLoading: boolean;
  isQualityError: boolean;
  qualityError: unknown;
  onQualityRetry: () => void;
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {summaryLoadingCardIds.map((id) => (
          <Card key={id}>
            <CardContent className="pt-6">
              <SectionLoading label="Sæki samantekt" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (isError || !summary) {
    return <SectionError title="Samantekt hlóðst ekki" error={error} onRetry={onRetry} />;
  }

  const dataGaps = getQualityGapCount(summary);
  const trackedEventCost = summary.tracked_event_cost_usd ?? summary.total_cost_usd;
  const estimatedAsrEventCost = summary.estimated_asr_event_cost_usd ?? 0;
  const azureCostAvailable =
    summary.azure_gpu_cost_status === 'available' && typeof summary.azure_gpu_cost_usd === 'number';
  const actualTotalAvailable = !hasActiveFilters && typeof summary.actual_total_cost_usd === 'number';
  const displayedTotal = actualTotalAvailable ? (summary.actual_total_cost_usd ?? trackedEventCost) : trackedEventCost;
  const totalDetail = actualTotalAvailable
    ? `${formatDecimal(summary.audio_minutes, 1)} hljóðmínútur · Azure ASR innifalið`
    : hasActiveFilters
      ? 'Sameiginlegur Azure ASR kostnaður er ekki innifalinn í síaðri samtölu'
      : 'Azure ASR reikningur er ekki tiltækur og því ekki innifalinn';

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
      <MetricCard
        title={actualTotalAvailable ? 'Heildarkostnaður (raun)' : 'Atburðakostnaður (áætlaður)'}
        value={formatCurrency(displayedTotal)}
        detail={totalDetail}
        icon={CircleDollarSign}
      />
      <MetricCard
        title="Kostnaður á hljóðmínútu"
        value={formatCostPerMinute(summary.total_cost_per_audio_minute)}
        detail={`${formatCostPerMinute(summary.asr_cost_per_minute)} ASR/mín`}
        icon={Timer}
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <MetricCard
            title="Azure ASR kostnaður (raun)"
            value={azureCostAvailable ? formatCurrency(summary.azure_gpu_cost_usd ?? 0) : 'Ekki tiltækt'}
            detail={`Allar stofnanir · ASR atburðaáætlun: ${formatCurrency(estimatedAsrEventCost)}`}
            icon={CircleDollarSign}
          >
            {!azureCostAvailable ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label="Sækja Azure ASR kostnað aftur"
                onClick={onRetry}
              >
                Reyna aftur
              </Button>
            ) : null}
          </MetricCard>
        </TooltipTrigger>
        <TooltipContent>
          {azureCostAvailable
            ? 'Raunkostnaður frá Azure fyrir ASR GPU þjónustuna á völdu tímabili, þar með talinn óvirkur tími.'
            : 'Azure Cost Management svaraði ekki. ASR atburðaáætlunin er aðeins til viðmiðunar.'}
        </TooltipContent>
      </Tooltip>
      <MetricCard
        title="Atburðir"
        value={formatNumber(summary.total_events)}
        detail={`${formatNumber(summary.total_tokens)} tokens`}
        icon={Hash}
      />
      <MetricCard title="Gagnagöt" value={formatNumber(dataGaps)} detail="Vantar kostnað eða lengd" icon={FileWarning}>
        {isQualityLoading && <SectionLoading label="Sæki gagnagæði" />}
        {isQualityError && (
          <SectionError title="Gagnagæði hlóðust ekki" error={qualityError} onRetry={onQualityRetry} />
        )}
        {!isQualityLoading && !isQualityError && <QualityBadges quality={quality} />}
      </MetricCard>
    </div>
  );
};
